<template>
  <div class="portal-page auth-page my-info-page">
    <div class="page-shell auth-page__shell">
      <header class="auth-page__head">
        <p class="auth-page__eyebrow">마이페이지</p>
        <h1 class="auth-page__title">내 정보</h1>
        <p class="auth-page__desc">
          필요한 항목만 적으면 됩니다. 배송지·연락처는 주문할 때, 환불 계좌는 취소·반품할 때도 받을 수 있습니다.
          이미 있는 이메일·휴대폰을 바꾸면 인증이 필요합니다.
        </p>
      </header>

      <form
        v-if="member"
        class="auth-page__panel auth-form my-info-form"
        @submit.prevent="onSubmit"
      >
        <div class="auth-form__body">
          <section class="auth-form__section">
            <h2 class="auth-form__section-title">계정</h2>

            <label v-if="isKakaoMember" class="auth-form__field">
              <span>로그인</span>
              <input
                :value="kakaoAccountLabel"
                type="text"
                disabled
              />
              <p class="auth-form__hint">카카오 계정으로 들어가셨습니다. 아이디와 비밀번호는 없습니다.</p>
            </label>
            <label v-else class="auth-form__field">
              <span>아이디</span>
              <input
                :value="member.userId"
                type="text"
                autocomplete="username"
                disabled
              />
              <p class="auth-form__hint">아이디는 변경할 수 없습니다.</p>
            </label>

            <div class="auth-form__field my-info-form__account-action">
              <router-link v-if="!isKakaoMember" to="/find-password" class="auth-form__link">
                비밀번호 변경
              </router-link>
              <router-link to="/mall/mypage" class="auth-form__link my-info-form__mall-link">
                함우리몰 마이쇼핑
              </router-link>
              <router-link to="/mall/mypage/orders" class="auth-form__link my-info-form__mall-link">
                주문내역
              </router-link>
              <router-link to="/mall/mypage/shipping" class="auth-form__link my-info-form__mall-link">
                배송지 관리
              </router-link>
            </div>
          </section>

          <section class="auth-form__section">
            <h2 class="auth-form__section-title">기관 정보</h2>
            <p class="auth-form__section-desc">나중에 적어도 됩니다. 주문에 필수는 아닙니다.</p>

            <label class="auth-form__field">
              <span>기관명</span>
              <input
                ref="orgNameRef"
                v-model="form.orgName"
                type="text"
                placeholder="기관명을 입력해 주세요"
                maxlength="50"
                :class="orgNameInputState"
                @input="onOrgNameInput"
                @keydown.enter.prevent="onOrgNameEnter"
              />
            </label>

            <label class="auth-form__field">
              <span>기관 기호</span>
              <input
                ref="orgCodeRef"
                v-model="form.orgCode"
                type="text"
                inputmode="numeric"
                autocomplete="off"
                placeholder="장기요양기관기호 11자리"
                maxlength="11"
                :class="orgCodeInputState"
                @input="onOrgCodeInput"
                @keydown.enter.prevent="onOrgCodeEnter"
              />
              <p class="auth-form__hint">
                국민건강보험공단에 등록된 장기요양기관기호 11자리 숫자입니다.
              </p>
            </label>

            <div class="auth-form__field">
              <span>사업 구분</span>
              <BizGubunPicker
                v-model="form.bizGubun"
                :options="bizGubunOptionsWithLegacy"
                :loading="bizGubunLoading"
                :load-error="bizGubunLoadError"
              />
            </div>

            <label class="auth-form__field">
              <span>사업자등록번호</span>
              <input
                :value="form.bizNum"
                type="text"
                inputmode="numeric"
                placeholder="000-00-00000"
                maxlength="12"
                :class="bizNumberFieldState(form.bizNum)"
                @input="form.bizNum = formatBizNumberInput(($event.target as HTMLInputElement).value)"
              />
              <p v-if="bizNumberInputError(form.bizNum)" class="auth-form__msg auth-form__msg--warn">
                {{ bizNumberInputError(form.bizNum) }}
              </p>
              <p v-else-if="isBizNumberInputValid(form.bizNum)" class="auth-form__msg auth-form__msg--ok">
                번호가 맞습니다.
              </p>
              <p v-else class="auth-form__hint">
                숫자만 넣으면 됩니다. 하이픈(-)은 알아서 들어갑니다.
              </p>
            </label>

            <div class="auth-form__field">
              <span>사업자등록증</span>
              <input
                type="file"
                accept=".jpg,.jpeg,.png,.webp,.pdf,image/*,application/pdf"
                :disabled="bizCertUploading"
                @change="onBizCertChange"
              />
              <p v-if="bizCertUploading" class="auth-form__msg auth-form__msg--muted">업로드 중...</p>
              <p v-else-if="form.bizCert" class="auth-form__msg auth-form__msg--ok">
                등록증이 등록되어 있습니다.
                <a :href="bizCertHref" target="_blank" rel="noopener" class="auth-form__link">미리보기</a>
              </p>
              <p v-else class="auth-form__hint">jpg · png · webp · pdf (최대 10MB)</p>
            </div>

            <div class="auth-form__field">
              <span>기관 주소</span>
              <div class="auth-form__addr-stack">
                <div class="auth-form__field-row">
                  <input
                    :value="form.zipcode"
                    type="text"
                    placeholder="우편번호"
                    readonly
                    :class="inputValidationState(form.zipcode, isZipcodeValid(form.zipcode))"
                    @click="onSearchAddress"
                  />
                  <button
                    type="button"
                    class="auth-form__inline-btn"
                    :disabled="postcodeOpening"
                    @click="onSearchAddress"
                  >
                    주소 검색
                  </button>
                </div>
                <input
                  :value="form.address"
                  type="text"
                  placeholder="주소 검색을 눌러 주세요"
                  readonly
                  :class="inputValidationState(form.address, isAddressValid(form.address))"
                  @click="onSearchAddress"
                />
                <input
                  v-model.trim="form.addressDetail"
                  type="text"
                  maxlength="100"
                  placeholder="상세 주소 (동·호수 등)"
                  :class="inputValidationState(form.addressDetail, isAddressDetailValid(form.addressDetail))"
                />
              </div>
              <p class="auth-form__hint">세금계산서·배송 안내에 사용됩니다.</p>
            </div>
          </section>

          <section class="auth-form__section">
            <h2 class="auth-form__section-title">환불 계좌</h2>
            <p class="auth-form__section-desc">
              지금 적어도 되고, 취소·반품할 때 적어도 됩니다. 그때 적은 계좌가 회원 환불계좌가 됩니다.
            </p>

            <label class="auth-form__field">
              <span>은행</span>
              <select v-model="form.refundBank">
                <option value="">은행을 선택해 주세요</option>
                <option v-for="bank in MEMBER_REFUND_BANKS" :key="bank" :value="bank">
                  {{ bank }}
                </option>
              </select>
            </label>

            <label class="auth-form__field">
              <span>계좌번호</span>
              <input
                v-model="form.refundAccount"
                type="text"
                inputmode="numeric"
                placeholder="숫자만 입력"
                maxlength="20"
                :class="inputValidationState(form.refundAccount, isRefundAccountValid(form.refundAccount))"
                @input="form.refundAccount = sanitizeDigitsInput(($event.target as HTMLInputElement).value, 20)"
              />
            </label>

            <label class="auth-form__field">
              <span>예금주</span>
              <input
                v-model.trim="form.refundHolder"
                type="text"
                placeholder="예금주명"
                maxlength="50"
                :class="inputValidationState(form.refundHolder, isRefundHolderValid(form.refundHolder))"
              />
            </label>
          </section>

          <section class="auth-form__section">
            <h2 class="auth-form__section-title">담당자</h2>

            <label class="auth-form__field">
              <span>이름</span>
              <input
                ref="nameRef"
                v-model="form.name"
                type="text"
                autocomplete="name"
                placeholder="이름을 입력해 주세요"
                maxlength="20"
                :class="nameInputState"
                @input="onNameInput"
                @blur="nameTouched = true"
                @keydown.enter.prevent="onNameEnter"
              />
              <p v-if="showNameWarning" class="auth-form__msg auth-form__msg--warn">
                한글 2자 이상 입력해 주세요.
              </p>
            </label>
          </section>

          <section class="auth-form__section">
            <h2 class="auth-form__section-title">연락처 · 본인 인증</h2>
            <p class="auth-form__section-desc">
              가입 시 인증한 연락처입니다. 변경할 때만 인증번호 확인이 필요합니다.
            </p>

            <div class="auth-form__field">
              <span>이메일</span>
              <div class="auth-form__field-row">
                <input
                  ref="emailRef"
                  v-model="form.email"
                  type="email"
                  autocomplete="email"
                  placeholder="example@email.com"
                  :class="emailInputState"
                  :disabled="isEmailInputLocked"
                  @keydown.enter.prevent="onEmailEnter"
                />
                <button
                  v-if="isEmailChanged"
                  type="button"
                  class="auth-form__inline-btn"
                  :class="{ 'auth-form__inline-btn--done': emailVerification.codeSent }"
                  :disabled="!isEmailValid(form.email) || emailVerification.verified || emailVerification.sending || emailVerification.resendIn > 0"
                  @click="sendEmailCode"
                >
                  {{ emailVerification.sendActionLabel('인증발송', '재발송') }}
                </button>
              </div>
              <p
                v-if="!isEmailChanged"
                class="auth-form__msg"
                :class="member.emailVerified ? 'auth-form__msg--ok' : 'auth-form__msg--muted'"
              >
                {{ member.emailVerified ? '인증된 이메일입니다.' : '등록된 이메일입니다.' }}
              </p>
              <p
                v-else-if="emailVerification.errorMessage && !emailVerification.codeSent"
                class="auth-form__msg auth-form__msg--warn"
              >
                {{ emailVerification.errorMessage }}
              </p>
              <p
                v-else-if="emailVerification.codeSent && !emailVerification.verified"
                class="auth-form__msg auth-form__msg--ok"
              >
                {{ form.email }}으로 인증번호를 보냈습니다. 받은편지함과 스팸함을 확인해 주세요.
              </p>
            </div>

            <div v-if="isEmailChanged && !emailVerification.verified" class="auth-form__field">
              <span>이메일 인증번호</span>
              <div class="auth-form__field-row">
                <input
                  ref="emailCodeRef"
                  v-model="form.emailCode"
                  type="text"
                  inputmode="numeric"
                  maxlength="6"
                  placeholder="인증번호 6자리"
                  :class="emailCodeInputState"
                  :disabled="!emailVerification.codeSent"
                  @input="onEmailCodeInput"
                  @keydown.enter.prevent="onEmailCodeEnter"
                />
                <button
                  type="button"
                  class="auth-form__inline-btn"
                  :disabled="!emailVerification.codeSent || !isVerifyCodeValid(form.emailCode) || emailVerification.verifying"
                  @click="verifyEmail"
                >
                  {{ emailVerification.verifying ? '확인중...' : '확인' }}
                </button>
              </div>
              <p v-if="!emailVerification.codeSent" class="auth-form__hint">
                변경할 이메일로 인증번호를 발송해 주세요.
              </p>
              <template v-else>
                <p class="auth-form__msg auth-form__msg--ok">
                  메일로 보낸 숫자 6자리를 적어 주세요.
                  <span v-if="emailVerification.timer > 0" class="auth-form__verify-timer">
                    {{ emailVerification.formatTimer(emailVerification.timer) }} 남음
                  </span>
                </p>
                <p v-if="emailVerification.errorMessage" class="auth-form__msg auth-form__msg--warn">
                  {{ emailVerification.errorMessage }}
                </p>
              </template>
            </div>
            <p v-if="isEmailChanged && emailVerification.verified" class="auth-form__msg auth-form__msg--ok">
              이메일 인증이 완료되었습니다.
            </p>

            <div class="auth-form__field">
              <span>휴대폰 번호</span>
              <div class="auth-form__field-row">
                <input
                  ref="phoneRef"
                  v-model="form.phone"
                  type="tel"
                  inputmode="numeric"
                  autocomplete="tel"
                  placeholder="01012345678"
                  maxlength="11"
                  :class="phoneInputState"
                  :disabled="isPhoneInputLocked"
                  @input="onPhoneInput"
                  @keydown.enter.prevent="onPhoneEnter"
                />
                <button
                  v-if="isPhoneChanged"
                  type="button"
                  class="auth-form__inline-btn"
                  :class="{ 'auth-form__inline-btn--done': phoneVerification.codeSent }"
                  :disabled="!isPhoneValid(form.phone) || phoneVerification.verified || phoneVerification.sending || phoneVerification.resendIn > 0"
                  @click="sendPhoneCode"
                >
                  {{ phoneVerification.sendActionLabel('인증발송', '재발송') }}
                </button>
              </div>
              <p
                v-if="!isPhoneChanged"
                class="auth-form__msg"
                :class="member.phoneVerified ? 'auth-form__msg--ok' : 'auth-form__msg--muted'"
              >
                {{ member.phoneVerified ? '인증된 휴대폰 번호입니다.' : '등록된 휴대폰 번호입니다.' }}
              </p>
              <p
                v-else-if="phoneVerification.errorMessage && !phoneVerification.codeSent"
                class="auth-form__msg auth-form__msg--warn"
              >
                {{ phoneVerification.errorMessage }}
              </p>
            </div>

            <div v-if="isPhoneChanged && !phoneVerification.verified" class="auth-form__field">
              <span>휴대폰 인증번호</span>
              <div class="auth-form__field-row">
                <input
                  ref="phoneCodeRef"
                  v-model="form.phoneCode"
                  type="text"
                  inputmode="numeric"
                  maxlength="6"
                  placeholder="인증번호 6자리"
                  :class="phoneCodeInputState"
                  :disabled="!phoneVerification.codeSent"
                  @input="onPhoneCodeInput"
                  @keydown.enter.prevent="onPhoneCodeEnter"
                />
                <button
                  type="button"
                  class="auth-form__inline-btn"
                  :disabled="!phoneVerification.codeSent || !isVerifyCodeValid(form.phoneCode) || phoneVerification.verifying"
                  @click="verifyPhone"
                >
                  {{ phoneVerification.verifying ? '확인중...' : '확인' }}
                </button>
              </div>
              <p v-if="!phoneVerification.codeSent" class="auth-form__hint">
                변경할 휴대폰 번호로 인증번호를 발송해 주세요.
              </p>
              <template v-else>
                <p class="auth-form__msg auth-form__msg--muted">
                  인증번호가 발송되었습니다.
                  <span v-if="phoneVerification.timer > 0" class="auth-form__verify-timer">
                    {{ phoneVerification.formatTimer(phoneVerification.timer) }}
                  </span>
                </p>
                <p v-if="phoneVerification.errorMessage" class="auth-form__msg auth-form__msg--warn">
                  {{ phoneVerification.errorMessage }}
                </p>
              </template>
            </div>
            <p v-if="isPhoneChanged && phoneVerification.verified" class="auth-form__msg auth-form__msg--ok">
              휴대폰 인증이 완료되었습니다.
            </p>
          </section>
        </div>

        <div class="auth-form__actions">
          <button type="submit" class="btn-primary auth-form__submit" :disabled="isSubmitting || !isDirty">
            {{ isSubmitting ? '저장 중...' : '저장' }}
          </button>
          <button type="button" class="btn-secondary auth-form__secondary" @click="onLogout">
            로그아웃
          </button>
        </div>
        <button type="button" class="my-info-form__withdraw" :disabled="withdrawing" @click="onWithdraw">
          {{ withdrawing ? '탈퇴 중...' : '계정 탈퇴' }}
        </button>
      </form>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { onBeforeRouteLeave, useRouter } from 'vue-router'
import {
  fetchBizGubunList,
  updateMemberProfile,
  uploadMemberBizCert,
  type BizGubunOption,
} from '@/api/member'
import BizGubunPicker from '@/components/member/BizGubunPicker.vue'
import { MEMBER_REFUND_BANKS } from '@/constants/banks'
import { useDaumPostcode } from '@/composables/useDaumPostcode'
import { useMemberAuth } from '@/composables/useMemberAuth'
import { useVerification } from '@/composables/useVerification'
import { filterBizGubunOptions, isExcludedBizGubun, parseBizGubun, serializeBizGubun } from '@/utils/bizGubun'
import {
  collectProfileValidationErrors,
  deferredInputValidationState,
  shouldShowNameFormatWarning,
  inputValidationState,
  isAddressDetailValid,
  isAddressValid,
  bizNumberFieldState,
  bizNumberInputError,
  formatBizNumberInput,
  isBizNumberInputValid,
  isEmailValid,
  isNameValid,
  isOrgCodeValid,
  isOrgNameValid,
  isPhoneValid,
  isRefundAccountValid,
  isRefundHolderValid,
  isVerifyCodeValid,
  isZipcodeValid,
  sanitizeDigitsInput,
  sanitizeNameInput,
  sanitizeOrgNameInput,
} from '@/utils/formValidation'

const API_ORIGIN = (process.env.VUE_APP_API_ORIGIN || '').replace(/\/$/, '')

const router = useRouter()
const { member, logout, withdraw, setMember } = useMemberAuth()
const isKakaoMember = computed(() => member.value?.loginType === 'kakao')
const kakaoAccountLabel = computed(() =>
  member.value?.oauthEmail ? `카카오 계정 · ${member.value.oauthEmail}` : '카카오 계정',
)
const withdrawing = ref(false)
const emailVerification = useVerification('profile')
const phoneVerification = useVerification('profile')
const { opening: postcodeOpening, searchPostcode } = useDaumPostcode()

const form = reactive({
  orgName: '',
  orgCode: '',
  bizGubun: [] as string[],
  name: '',
  email: '',
  emailCode: '',
  phone: '',
  phoneCode: '',
  bizNum: '',
  bizCert: '',
  zipcode: '',
  address: '',
  addressDetail: '',
  refundBank: '',
  refundAccount: '',
  refundHolder: '',
})

const bizCertUploading = ref(false)
const bizCertHref = computed(() =>
  form.bizCert ? `${API_ORIGIN}${form.bizCert}` : '',
)

const originalEmail = ref('')
const originalPhone = ref('')
const originalSnapshot = ref('')
const isSubmitting = ref(false)
const skipLeaveGuard = ref(false)
const bizGubunOptions = ref<BizGubunOption[]>([])
const bizGubunLoading = ref(false)
const bizGubunLoadError = ref('')
const nameTouched = ref(false)
const formSubmitAttempted = ref(false)

const orgNameRef = ref<HTMLInputElement | null>(null)
const orgCodeRef = ref<HTMLInputElement | null>(null)
const nameRef = ref<HTMLInputElement | null>(null)
const emailRef = ref<HTMLInputElement | null>(null)
const emailCodeRef = ref<HTMLInputElement | null>(null)
const phoneRef = ref<HTMLInputElement | null>(null)
const phoneCodeRef = ref<HTMLInputElement | null>(null)

const focusInput = (target: { value: HTMLInputElement | HTMLSelectElement | null }) => {
  target.value?.focus()
}

const normalizeEmail = (value: string) => value.trim().toLowerCase()

const snapshotForm = () =>
  JSON.stringify({
    orgName: form.orgName,
    orgCode: form.orgCode,
    bizGubun: serializeBizGubun(form.bizGubun),
    name: form.name,
    email: form.email,
    phone: form.phone,
    bizNum: form.bizNum,
    bizCert: form.bizCert,
    zipcode: form.zipcode,
    address: form.address,
    addressDetail: form.addressDetail,
    refundBank: form.refundBank,
    refundAccount: form.refundAccount,
    refundHolder: form.refundHolder,
  })

const isDirty = computed(() => snapshotForm() !== originalSnapshot.value)

const isEmailChanged = computed(() => normalizeEmail(form.email) !== originalEmail.value)
const isPhoneChanged = computed(() => form.phone !== originalPhone.value)

const isEmailReady = computed(() => !isEmailChanged.value || emailVerification.verified)
const isPhoneReady = computed(() => !isPhoneChanged.value || phoneVerification.verified)

const bizGubunOptionsWithLegacy = computed(() => {
  const options = [...bizGubunOptions.value]

  parseBizGubun(member.value?.bizGubun).forEach((value) => {
    if (isExcludedBizGubun(value)) {
      return
    }

    if (!options.some((item) => item.value === value)) {
      options.push({
        value,
        label: `${value} (기존)`,
        groupName: '',
      })
    }
  })

  return filterBizGubunOptions(options)
})

const orgNameInputState = computed(() =>
  inputValidationState(form.orgName, isOrgNameValid(form.orgName)),
)

const orgCodeInputState = computed(() =>
  inputValidationState(form.orgCode, isOrgCodeValid(form.orgCode)),
)

const nameInputState = computed(() =>
  deferredInputValidationState(
    form.name,
    isNameValid(form.name),
    nameTouched.value,
    formSubmitAttempted.value,
  ),
)

const showNameWarning = computed(() =>
  shouldShowNameFormatWarning(form.name, nameTouched.value, formSubmitAttempted.value),
)

const emailInputState = computed(() =>
  inputValidationState(form.email, {
    isValid: isEmailReady.value,
    isInvalid: Boolean(form.email) && !isEmailValid(form.email),
  }),
)

const emailCodeInputState = computed(() =>
  inputValidationState(form.emailCode, isVerifyCodeValid(form.emailCode)),
)

const phoneInputState = computed(() =>
  inputValidationState(form.phone, {
    isValid: isPhoneReady.value,
    isInvalid: Boolean(form.phone) && !isPhoneValid(form.phone),
  }),
)

const phoneCodeInputState = computed(() =>
  inputValidationState(form.phoneCode, isVerifyCodeValid(form.phoneCode)),
)

const isEmailInputLocked = computed(
  () =>
    isEmailChanged.value &&
    (emailVerification.sending || emailVerification.verified || emailVerification.codeSent),
)

const isPhoneInputLocked = computed(
  () =>
    isPhoneChanged.value &&
    (phoneVerification.sending || phoneVerification.verified || phoneVerification.codeSent),
)

const applyMemberToForm = () => {
  if (!member.value) {
    return
  }

  form.orgName = member.value.orgName || ''
  form.orgCode = member.value.longtermCode || ''
  form.bizGubun = parseBizGubun(member.value.bizGubun)
  form.name = member.value.bossName || ''
  form.email = member.value.bossEmail || ''
  form.phone = member.value.bossPhone || ''
  form.bizNum = formatBizNumberInput(member.value.bizNum || '')
  form.bizCert = member.value.bizCert || ''
  form.zipcode = String(member.value.zipcode || '').replace(/\D/g, '').slice(0, 5)
  form.address = member.value.address || ''
  form.addressDetail = member.value.addressDetail || ''
  form.refundBank = member.value.refundBank || ''
  form.refundAccount = String(member.value.refundAccount || '').replace(/\D/g, '')
  form.refundHolder = member.value.refundHolder || ''
  form.emailCode = ''
  form.phoneCode = ''
  originalEmail.value = normalizeEmail(form.email)
  originalPhone.value = form.phone
  emailVerification.reset()
  phoneVerification.reset()
  originalSnapshot.value = snapshotForm()
}

const onBizCertChange = async (event: Event) => {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  if (!file) return

  bizCertUploading.value = true
  try {
    const uploaded = await uploadMemberBizCert(file)
    form.bizCert = uploaded.url
  } catch (error) {
    window.alert(error instanceof Error ? error.message : '사업자등록증 업로드에 실패했습니다.')
  } finally {
    bizCertUploading.value = false
    input.value = ''
  }
}

async function onSearchAddress() {
  try {
    const result = await searchPostcode()
    if (!result) return
    form.zipcode = result.zipcode
    form.address = result.extra ? `${result.address} ${result.extra}` : result.address
  } catch (error) {
    window.alert(error instanceof Error ? error.message : '주소 검색에 실패했습니다.')
  }
}

watch(
  () => member.value?.mCode,
  () => {
    applyMemberToForm()
  },
  { immediate: true },
)

watch(
  () => form.email,
  () => {
    form.emailCode = ''
    emailVerification.reset()
  },
)

watch(
  () => form.phone,
  () => {
    form.phoneCode = ''
    phoneVerification.reset()
  },
)

onBeforeRouteLeave(() => {
  if (skipLeaveGuard.value || !isDirty.value) {
    return true
  }

  return window.confirm('저장하지 않은 변경 내용이 있습니다.\n다른 페이지로 이동하시겠습니까?')
})

onMounted(() => {
  loadBizGubunOptions()
})

const loadBizGubunOptions = async () => {
  bizGubunLoading.value = true
  bizGubunLoadError.value = ''

  try {
    bizGubunOptions.value = await fetchBizGubunList()
  } catch (error) {
    bizGubunLoadError.value =
      error instanceof Error ? error.message : '사업구분 목록을 불러오지 못했습니다.'
  } finally {
    bizGubunLoading.value = false
  }
}

const onOrgNameInput = (event: Event) => {
  form.orgName = sanitizeOrgNameInput((event.target as HTMLInputElement).value)
}

const onOrgCodeInput = (event: Event) => {
  form.orgCode = sanitizeDigitsInput((event.target as HTMLInputElement).value, 11)
}

const onNameInput = (event: Event) => {
  form.name = sanitizeNameInput((event.target as HTMLInputElement).value)
}

const onPhoneInput = (event: Event) => {
  form.phone = sanitizeDigitsInput((event.target as HTMLInputElement).value, 11)
}

const onEmailCodeInput = (event: Event) => {
  form.emailCode = sanitizeDigitsInput((event.target as HTMLInputElement).value, 6)
}

const onPhoneCodeInput = (event: Event) => {
  form.phoneCode = sanitizeDigitsInput((event.target as HTMLInputElement).value, 6)
}

const onOrgNameEnter = () => {
  focusInput(orgCodeRef)
}

const onOrgCodeEnter = () => {
  if (isOrgCodeValid(form.orgCode)) {
    focusInput(nameRef)
  }
}

const onNameEnter = () => {
  focusInput(emailRef)
}

const onEmailEnter = () => {
  if (isEmailChanged.value && !emailVerification.verified) {
    focusInput(emailCodeRef)
    return
  }
  focusInput(phoneRef)
}

const onEmailCodeEnter = () => {
  verifyEmail()
}

const onPhoneEnter = () => {
  if (isPhoneChanged.value && !phoneVerification.verified) {
    focusInput(phoneCodeRef)
    return
  }
  onSubmit()
}

const onPhoneCodeEnter = () => {
  verifyPhone()
}

const sendEmailCode = async () => {
  try {
    await emailVerification.sendEmail(form.email)
  } catch {
    // errorMessage는 폼에 표시
  }
}

const verifyEmail = async () => {
  try {
    await emailVerification.verifyEmail(form.email, form.emailCode)
  } catch {
    // errorMessage는 폼에 표시
  }
}

const sendPhoneCode = async () => {
  try {
    await phoneVerification.sendPhone(form.phone)
  } catch {
    // errorMessage는 폼에 표시
  }
}

const verifyPhone = async () => {
  try {
    await phoneVerification.verifyPhone(form.phone, form.phoneCode)
  } catch {
    // errorMessage는 폼에 표시
  }
}

const onSubmit = () => {
  formSubmitAttempted.value = true

  if (!isDirty.value) {
    window.alert('변경된 내용이 없습니다.')
    return
  }

  const errors = collectProfileValidationErrors({
    form,
    emailChanged: isEmailChanged.value,
    phoneChanged: isPhoneChanged.value,
    emailVerified: isEmailReady.value,
    phoneVerified: isPhoneReady.value,
  })

  if (errors.length > 0) {
    window.alert(`다음 항목을 확인해 주세요.\n\n${errors.map((item) => `• ${item}`).join('\n')}`)
    return
  }

  if (!window.confirm('변경한 내용을 저장하시겠습니까?')) {
    return
  }

  saveProfile()
}

const saveProfile = async () => {
  if (isSubmitting.value) {
    return
  }

  isSubmitting.value = true

  try {
    const updatedMember = await updateMemberProfile({
      orgName: form.orgName,
      orgCode: form.orgCode,
      bizGubun: serializeBizGubun(form.bizGubun),
      name: form.name,
      email: form.email,
      phone: form.phone,
      bizNum: form.bizNum,
      bizCert: form.bizCert,
      zipcode: form.zipcode,
      address: form.address,
      addressDetail: form.addressDetail,
      refundBank: form.refundBank,
      refundAccount: form.refundAccount,
      refundHolder: form.refundHolder,
    })

    setMember(updatedMember)
    applyMemberToForm()
    skipLeaveGuard.value = true
    window.alert('내 정보가 저장되었습니다.')
  } catch (error) {
    const message = error instanceof Error ? error.message : '내 정보 저장에 실패했습니다.'
    window.alert(message)
  } finally {
    isSubmitting.value = false
  }
}

const onLogout = async () => {
  if (isDirty.value && !window.confirm('저장하지 않은 변경 내용이 있습니다.\n로그아웃하시겠습니까?')) {
    return
  }

  skipLeaveGuard.value = true
  await logout()
  await router.push({ name: 'dashboard' })
}

const onWithdraw = async () => {
  if (withdrawing.value) {
    return
  }
  if (
    !window.confirm(
      '정말 탈퇴할까요?\n카카오 계정이면 같은 카카오로 다시 가입할 수 있습니다.\n주문 기록은 보관됩니다.',
    )
  ) {
    return
  }
  withdrawing.value = true
  skipLeaveGuard.value = true
  try {
    await withdraw()
    window.alert('탈퇴가 완료되었습니다.')
    await router.push({ name: 'dashboard' })
  } catch (error) {
    skipLeaveGuard.value = false
    window.alert(error instanceof Error ? error.message : '탈퇴에 실패했습니다.')
  } finally {
    withdrawing.value = false
  }
}
</script>

<style lang="scss" src="./MyInfoPage.scss"></style>
